/**
 * NEW Custom Fields PDF Renderer - Separates questions from photos
 * Questions: Batched 10 per page
 * Photos: Batched 6 per page with proper frames
 */

import type { FormTemplate, PageSection, FieldSchema } from '../types/formSchema';

interface PDFRenderOptions {
  pageNumber: number;
  totalPages: number;
  generateHeader: (surveyData: any, pageNumber: number, totalPages: number, options?: any) => string;
  generateFooter?: (options?: any) => string;
  surveyData: any;
  formData: Record<string, any>;
  options?: any;
}

/**
 * Render a complete custom page for PDF
 * STEP 1: Render all fields/questions (10 per page)
 * STEP 2: Render all photos separately (6 per page)
 */
export function renderCustomPageForPDF(
  page: PageSection,
  renderOptions: PDFRenderOptions
): string[] {
  const { pageNumber, totalPages, generateHeader, generateFooter, surveyData, formData, options } = renderOptions;
  
  console.log(`[PDF Renderer] Rendering custom page "${page.title}" (${page.id})`);
  console.log(`[PDF Renderer] Starting page number: ${pageNumber}, Total pages: ${totalPages}`);
  
  if (!page.showInPDF) {
    return [];
  }
  
  const pages: string[] = [];
  
  // Sort fields by order
  const sortedFields = [...page.fields].sort((a, b) => a.order - b.order);
  
  // ===== STEP 1: Collect all photos from all fields =====
  const allPhotos: Array<{ url: string; label: string; fieldLabel: string }> = [];
  
  for (const field of sortedFields) {
    // Check for photo attachments (fields with enablePhotoCapture)
    if ((field as any).enablePhotoCapture) {
      const photosFieldId = `${field.id}_photos`;
      const photos = formData[photosFieldId];
      if (photos && Array.isArray(photos) && photos.length > 0) {
        photos.forEach((photo, idx) => {
          const photoUrl = typeof photo === 'string' ? photo : (photo.cdnUrl || photo.url || photo.dataUrl);
          const customCaption = typeof photo === 'object' ? (photo.caption || photo.notes || photo.note || '') : '';
          if (photoUrl) {
            allPhotos.push({
              url: photoUrl,
              label: customCaption || `${field.label} (${idx + 1}/${photos.length})`,
              fieldLabel: field.label
            });
          }
        });
      }
    }
    
    // Check for image type fields
    if (field.type === 'image') {
      const images = formData[field.id];
      if (images && Array.isArray(images) && images.length > 0) {
        images.forEach((img, idx) => {
          const imgUrl = typeof img === 'string' ? img : (img.cdnUrl || img.url || img.dataUrl);
          const caption = typeof img === 'object' ? (img.caption || img.notes || '') : '';
          if (imgUrl) {
            allPhotos.push({
              url: imgUrl,
              label: caption || `${field.label} (${idx + 1}/${images.length})`,
              fieldLabel: field.label
            });
          }
        });
      }
    }
  }
  
  console.log(`[PDF Renderer] Collected ${allPhotos.length} total photos from all fields`);
  
  // ===== STEP 2: Render all fields (questions/data) without photos =====
  const fieldsToRender = sortedFields.filter(field => {
    const value = formData[field.id];
    return value !== null && value !== undefined && value !== '' || field.required;
  });
  
  const fieldsPerPage = 10;
  const totalFieldPages = Math.ceil(fieldsToRender.length / fieldsPerPage);
  
  console.log(`[PDF Renderer] Rendering ${fieldsToRender.length} fields across ${totalFieldPages} page(s)`);
  
  // Track current page number as we render
  let currentPageNumber = pageNumber;
  
  for (let pageIdx = 0; pageIdx < totalFieldPages; pageIdx++) {
    const pageFields = fieldsToRender.slice(pageIdx * fieldsPerPage, (pageIdx + 1) * fieldsPerPage);
    const isFirstPage = pageIdx === 0;
    
    let html = `
      <section class="pdf-page" id="${isFirstPage ? page.id : `${page.id}_fields_${pageIdx}`}">
        ${generateHeader(surveyData, currentPageNumber++, totalPages, options)}
        
        <div class="pdf-content">
          <div class="section-heading-wrapper">
            <div class="section-icon-bar"></div>
            <h3>${page.title}${totalFieldPages > 1 ? ` (${pageIdx + 1}/${totalFieldPages})` : ''}</h3>
          </div>
          ${isFirstPage && page.description ? `<p class="section-description">${page.description}</p>` : ''}
          
          <div class="custom-fields-container">`;
    
    // Render fields for this page (WITHOUT photos)
    for (const field of pageFields) {
      const value = formData[field.id];
      html += renderFieldWithoutPhotos(field, value);
    }
    
    html += `
          </div>
        </div>
        ${generateFooter ? generateFooter(options) : ''}
      </section>`;
    
    pages.push(html);
  }
  
  // ===== STEP 3: Render all photos separately (6 per page) =====
  if (allPhotos.length > 0) {
    const photosPerPage = 6;
    const totalPhotoPages = Math.ceil(allPhotos.length / photosPerPage);
    
    console.log(`[PDF Renderer] Rendering ${allPhotos.length} photos across ${totalPhotoPages} page(s)`);
    
    for (let pageIdx = 0; pageIdx < totalPhotoPages; pageIdx++) {
      const pagePhotos = allPhotos.slice(pageIdx * photosPerPage, (pageIdx + 1) * photosPerPage);
      
      let photoGridHtml = '<div class="property-photo-grid" style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 12pt; margin-top: 12pt;">';
      
      pagePhotos.forEach((photo) => {
        photoGridHtml += `
          <div class="property-photo-wrapper">
            <div class="property-photo-item">
              <img 
                src="${escapeHtml(photo.url)}" 
                alt="${escapeHtml(photo.label)}" 
              />
            </div>
            <div class="property-photo-label">${escapeHtml(photo.label)}</div>
          </div>`;
      });
      
      photoGridHtml += '</div>';
      
      const photoPageHtml = `
        <section class="pdf-page" id="${page.id}_photos_${pageIdx}">
          ${generateHeader(surveyData, currentPageNumber++, totalPages, options)}
          
          <div class="pdf-content">
            <div class="section-heading-wrapper">
              <div class="section-icon-bar"></div>
              <h3>${page.title}: Photo Evidence${totalPhotoPages > 1 ? ` (${pageIdx + 1}/${totalPhotoPages})` : ''}</h3>
            </div>
            ${photoGridHtml}
          </div>
          ${generateFooter ? generateFooter(options) : ''}
        </section>`;
      
      pages.push(photoPageHtml);
    }
  }
  
  console.log(`✅ [PDF Renderer] Generated ${pages.length} total page(s) for "${page.title}":`);
  console.log(`   - Field pages: ${totalFieldPages}`);
  console.log(`   - Photo pages: ${allPhotos.length > 0 ? Math.ceil(allPhotos.length / 6) : 0}`);
  
  return pages;
}

/**
 * Render a single field WITHOUT photos
 */
function renderFieldWithoutPhotos(field: FieldSchema, value: any): string {
  // Skip fields with no value unless required
  if (!value && !field.required && value !== 0 && value !== false) {
    return '';
  }

  let html = `
    <div class="field-group">
      <div class="field-label">
        ${field.label}${field.required ? '<span class="required">*</span>' : ''}
        ${field.type === 'number' && (field as any).unit ? ` <span class="unit">(${(field as any).unit})</span>` : ''}
      </div>
      <div class="field-value">`;

  // Render based on field type
  switch (field.type) {
    case 'text':
    case 'textarea':
      html += value ? escapeHtml(String(value)) : '—';
      break;
    
    case 'number':
      html += value !== null && value !== undefined ? String(value) : '—';
      break;
    
    case 'date':
      if (value) {
        try {
          const date = new Date(value);
          html += date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        } catch {
          html += escapeHtml(String(value));
        }
      } else {
        html += '—';
      }
      break;
    
    case 'dropdown':
      const dropdownOptions = (field as any).options || [];
      const option = dropdownOptions.find((opt: any) => opt.value === value);
      html += option ? escapeHtml(option.label) : (value ? escapeHtml(String(value)) : '—');
      break;
    
    case 'multiChoice':
      const multiOptions = (field as any).options || [];
      const values = Array.isArray(value) ? value : [value];
      const labels = values.map((val: any) => {
        const opt = multiOptions.find((o: any) => o.value === val);
        return opt ? opt.label : String(val);
      });
      html += labels.length > 0 ? labels.map((l: string) => `✓ ${escapeHtml(l)}`).join('<br>') : '—';
      break;
    
    case 'boolean':
      html += value ? 'Yes' : 'No';
      break;
    
    case 'image':
      // Just show count, actual images rendered separately
      const images = Array.isArray(value) ? value : [];
      html += images.length > 0 ? `${images.length} image(s) - see photo evidence pages` : 'No images';
      break;
    
    default:
      html += value ? escapeHtml(String(value)) : '—';
  }

  html += `
      </div>
    </div>`;

  return html;
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Calculate how many PDF pages a custom page will take
 * Matches the new rendering logic: fields batched 10 per page, photos 6 per page
 */
export function calculateCustomPageCount(
  page: PageSection,
  formData: Record<string, any>
): number {
  if (!page.showInPDF) {
    return 0;
  }
  
  const sortedFields = [...page.fields].sort((a, b) => a.order - b.order);
  
  // ===== Count field pages (10 fields per page) =====
  const fieldsToRender = sortedFields.filter(field => {
    const value = formData[field.id];
    return value !== null && value !== undefined && value !== '' || field.required;
  });
  
  const fieldsPerPage = 10;
  const fieldPages = Math.ceil(fieldsToRender.length / fieldsPerPage);
  
  // ===== Count all photos =====
  let totalPhotos = 0;
  
  for (const field of sortedFields) {
    // Check for photo attachments (fields with enablePhotoCapture)
    if ((field as any).enablePhotoCapture) {
      const photosFieldId = `${field.id}_photos`;
      const photos = formData[photosFieldId];
      if (photos && Array.isArray(photos)) {
        totalPhotos += photos.length;
      }
    }
    
    // Check for image type fields
    if (field.type === 'image') {
      const images = formData[field.id];
      if (images && Array.isArray(images)) {
        totalPhotos += images.length;
      }
    }
  }
  
  const photosPerPage = 6;
  const photoPages = totalPhotos > 0 ? Math.ceil(totalPhotos / photosPerPage) : 0;
  
  const totalPages = fieldPages + photoPages;
  
  console.log(`[Page Count] Custom page "${page.title}": ${totalPages} page(s) (${fieldPages} field + ${photoPages} photo)`);
  
  return totalPages;
}

// Re-export other functions from original renderer
export { getCustomPagesForPDF, calculateTotalCustomPagesCount, getCustomFieldsPDFStyles } from './customFieldsPDFRenderer';
